/**
 * Select — custom dropdown replacing native <select> elements.
 *
 * Two variants via the `searchable` prop:
 *   false (default) — plain list, good for short option sets (2–8 items)
 *   true            — adds a filter input, good for longer lists (8+ items)
 *
 * Props:
 *   value        — currently selected value (string)
 *   onChange(v)  — called with the new value string on selection
 *   options      — [{ value: string, label: string }]
 *   placeholder  — shown when nothing is selected (default '— select —')
 *   searchable   — boolean (default false)
 *   disabled     — boolean
 *   dark         — boolean — use when rendered inside a dark-mode parent
 *   style        — style overrides for the trigger button (e.g. width, padding, fontSize)
 *
 * Usage:
 *   <Select
 *     value={seatTypeId}
 *     onChange={setSeatTypeId}
 *     options={seatTypes.map(st => ({ value: st.id, label: st.name }))}
 *     dark={dark}
 *   />
 *
 *   <Select
 *     value={discipline}
 *     onChange={setDiscipline}
 *     options={DISCIPLINE_ORDER.map(d => ({ value: d, label: d }))}
 *     searchable
 *     style={{ fontSize: 12 }}
 *   />
 */

import { useState, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { useDarkMode } from '../outline/theme.js'

export default function Select({
  value,
  onChange,
  options = [],
  placeholder = '— select —',
  searchable = false,
  disabled = false,
  dark,          // optional override; omit to follow the app's current theme automatically
  style,
}) {
  // Most callers never passed `dark` at all, so this dropdown silently stayed light-styled inside
  // an otherwise-dark app. Follow the real theme unless a caller explicitly overrides it (e.g. a
  // Select rendered inside a permanently-dark surface regardless of the app-wide theme).
  const autoDark = useDarkMode()
  dark = dark === undefined ? autoDark : dark
  const [open,        setOpen]        = useState(false)
  const [query,       setQuery]       = useState('')
  const [highlighted, setHighlighted] = useState(-1)
  const [pos,         setPos]         = useState({ top: 0, left: 0, width: 0, above: false })

  const triggerRef = useRef(null)
  const menuRef    = useRef(null)
  const inputRef   = useRef(null)

  const selected = options.find(o => o.value === value)
  const filtered = searchable && query
    ? options.filter(o => o.label.toLowerCase().includes(query.toLowerCase()))
    : options

  /* ── Theme-aware palette ─────────────────────────────────────────────────── */
  const c = {
    trigBg:    dark ? 'rgba(255,255,255,0.05)'   : '#f4f7f8',
    trigBdr:   dark ? 'rgba(255,255,255,0.12)'   : 'rgba(7,39,45,0.18)',
    trigBdrOn: dark ? 'rgba(207,226,232,0.35)'   : 'rgba(7,39,45,0.35)',
    text:      dark ? 'rgba(207,226,232,0.92)'   : '#07272D',
    muted:     dark ? 'rgba(91,132,148,0.80)'    : 'rgba(7,39,45,0.38)',
    menuBg:    dark ? '#0d1e24'                   : '#ffffff',
    menuBdr:   dark ? 'rgba(255,255,255,0.10)'   : 'rgba(7,39,45,0.12)',
    divider:   dark ? 'rgba(255,255,255,0.07)'   : 'rgba(7,39,45,0.08)',
    hover:     dark ? 'rgba(255,255,255,0.06)'   : '#f0f8f5',
    selBg:     dark ? 'rgba(29,158,117,0.18)'    : '#eaf8f2',
    accent:    '#1D9E75',
  }

  /* ── Open / close helpers ────────────────────────────────────────────────── */
  function openMenu() {
    if (disabled) return
    const rect = triggerRef.current?.getBoundingClientRect()
    if (!rect) return
    const ITEM_H   = 36
    const SEARCH_H = 44
    const MAX_H    = 240
    const estH = Math.min(filtered.length * ITEM_H + (searchable ? SEARCH_H : 0) + 8, MAX_H)
    const above = window.innerHeight - rect.bottom < estH + 6 && rect.top > estH + 6
    setPos({ top: above ? rect.top : rect.bottom, left: rect.left, width: rect.width, above })
    setHighlighted(options.findIndex(o => o.value === value))
    setQuery('')
    setOpen(true)
  }

  function closeMenu() { setOpen(false) }

  function select(v) {
    onChange(v)
    closeMenu()
    triggerRef.current?.focus()
  }

  /* ── Auto-focus search input when opened ─────────────────────────────────── */
  useEffect(() => {
    if (open && searchable) setTimeout(() => inputRef.current?.focus(), 0)
  }, [open, searchable])

  /* ── Close on outside click ──────────────────────────────────────────────── */
  useEffect(() => {
    if (!open) return
    function handler(e) {
      if (
        !triggerRef.current?.contains(e.target) &&
        !menuRef.current?.contains(e.target)
      ) closeMenu()
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  /* ── Close on scroll — only when scroll originates outside the menu ────── */
  useEffect(() => {
    if (!open) return
    function handler(e) {
      if (menuRef.current?.contains(e.target)) return // scrolling inside the list — keep open
      closeMenu()
    }
    window.addEventListener('scroll', handler, true)
    return () => window.removeEventListener('scroll', handler, true)
  }, [open])

  /* ── Keyboard: trigger ───────────────────────────────────────────────────── */
  function onTriggerKey(e) {
    if (['Enter', ' ', 'ArrowDown', 'ArrowUp'].includes(e.key)) {
      e.preventDefault()
      if (!open) openMenu()
    }
    if (e.key === 'Escape' && open) closeMenu()
  }

  /* ── Keyboard: menu ──────────────────────────────────────────────────────── */
  function onMenuKey(e) {
    if (e.key === 'Escape') { closeMenu(); triggerRef.current?.focus() }
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlighted(h => Math.min(h + 1, filtered.length - 1))
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlighted(h => Math.max(h - 1, 0))
    }
    if (e.key === 'Enter') {
      e.preventDefault()
      const opt = filtered[highlighted >= 0 ? highlighted : 0]
      if (opt) select(opt.value)
    }
  }

  /* ── Trigger styles ──────────────────────────────────────────────────────── */
  const triggerStyle = {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    fontFamily: '"DM Sans", sans-serif', fontSize: 13,
    color: selected ? c.text : c.muted,
    background: c.trigBg,
    border: `0.5px solid ${open ? c.trigBdrOn : c.trigBdr}`,
    borderRadius: 4, padding: '7px 10px',
    cursor: disabled ? 'not-allowed' : 'pointer',
    outline: 'none', textAlign: 'left',
    opacity: disabled ? 0.55 : 1,
    transition: 'border-color 0.1s',
    userSelect: 'none',
    boxSizing: 'border-box',
    ...style,
  }

  /* ── Menu position ───────────────────────────────────────────────────────── */
  const menuStyle = {
    position: 'fixed',
    ...(pos.above
      ? { bottom: window.innerHeight - pos.top + 4 }
      : { top: pos.top + 4 }
    ),
    left: pos.left,
    width: pos.width,
    background: c.menuBg,
    border: `0.5px solid ${c.menuBdr}`,
    borderRadius: 10,
    boxShadow: '0 6px 28px rgba(7,39,45,0.14)',
    zIndex: 1000200,   // above the gate scrims (1000000) and Dialog (1000100) — a Select inside the triage gate must open on top (Matt, 2026-09-10)
    overflow: 'hidden',
    display: 'flex', flexDirection: 'column',
    maxHeight: 240,
  }

  return (
    <>
      {/* ── Trigger ────────────────────────────────────────────────────────── */}
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        onClick={() => open ? closeMenu() : openMenu()}
        onKeyDown={onTriggerKey}
        style={triggerStyle}
      >
        <span style={{
          flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {selected ? selected.label : placeholder}
        </span>
        {/* Chevron */}
        <svg
          width="10" height="10" viewBox="0 0 10 10" fill="none"
          style={{
            flexShrink: 0, color: c.muted,
            transition: 'transform 0.15s',
            transform: open ? 'rotate(180deg)' : 'none',
          }}
        >
          <path d="M2 3.5L5 6.5L8 3.5" stroke="currentColor" strokeWidth="1.4"
            strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>

      {/* ── Dropdown menu (portaled to body) ───────────────────────────────── */}
      {open && createPortal(
        <div ref={menuRef} onKeyDown={onMenuKey} style={menuStyle}>

          {/* Search input */}
          {searchable && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '8px 12px', borderBottom: `0.5px solid ${c.divider}`,
              flexShrink: 0,
            }}>
              <svg width="12" height="12" viewBox="0 0 16 16" fill="none"
                style={{ flexShrink: 0, color: c.muted }}>
                <circle cx="6.5" cy="6.5" r="4.5" stroke="currentColor" strokeWidth="1.5"/>
                <line x1="10" y1="10" x2="14" y2="14" stroke="currentColor"
                  strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
              <input
                ref={inputRef}
                value={query}
                onChange={e => { setQuery(e.target.value); setHighlighted(0) }}
                onKeyDown={onMenuKey}
                placeholder="Search…"
                style={{
                  flex: 1, minWidth: 0, border: 'none', outline: 'none',
                  background: 'transparent',
                  fontFamily: '"DM Sans", sans-serif', fontSize: 13, color: c.text,
                }}
              />
              {query && (
                <button
                  onClick={() => {
                    setQuery('')
                    setHighlighted(0)
                    inputRef.current?.focus()
                  }}
                  style={{
                    background: 'none', border: 'none', cursor: 'pointer',
                    color: c.muted, padding: 0, lineHeight: 1, fontSize: 16,
                  }}
                >×</button>
              )}
            </div>
          )}

          {/* Options list */}
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {filtered.length === 0 ? (
              <div style={{
                padding: '12px 14px',
                fontFamily: '"DM Sans", sans-serif', fontSize: 13, color: c.muted,
              }}>
                No results
              </div>
            ) : filtered.map((opt, idx) => {
              const isSel = opt.value === value
              const isHi  = idx === highlighted
              return (
                <div
                  key={opt.value}
                  onMouseEnter={() => setHighlighted(idx)}
                  onClick={() => select(opt.value)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    padding: '8px 14px',
                    background: isSel ? c.selBg : isHi ? c.hover : 'transparent',
                    cursor: 'pointer',
                    fontFamily: '"DM Sans", sans-serif', fontSize: 13, color: c.text,
                    transition: 'background 0.08s',
                  }}
                >
                  {/* Checkmark — always occupies space; visible only when selected */}
                  <svg
                    width="12" height="12" viewBox="0 0 12 12" fill="none"
                    style={{ flexShrink: 0, color: c.accent, opacity: isSel ? 1 : 0 }}
                  >
                    <path d="M2 6L5 9L10 3" stroke="currentColor" strokeWidth="1.5"
                      strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
                    {opt.label}
                  </span>
                </div>
              )
            })}
          </div>
        </div>,
        document.body
      )}
    </>
  )
}
